import { Box, Link, Typography } from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";

import ContactListItem from "features/Influencer/QuickSendIndividualDrawer/PreviewPerContact/PreviewIndividual/ContactListItem";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

const CHAT_MONTHLY_LIMIT = 500;

export default function UpgradeFeatureDescription() {
  const { upgradeDialogSource, subscription, brandRequestsMade } =
    useContext(SubscriptionContext);
  const { handleCheckout } = useSubscription();
  if (upgradeDialogSource === UpgradeDialogSource.hits_limit) {
    return (
      <Typography paragraph textAlign="center">
        Your current plan only allows you to send{" "}
        <strong>{subscription?.monthlyLimit} emails</strong> per month but you
        are attempting to send more than that. Please upgrade.
      </Typography>
    );
  } else if (upgradeDialogSource === UpgradeDialogSource.hits_campaign_limit) {
    return (
      <Typography paragraph textAlign="center">
        You can only send{" "}
        <Box component="span" sx={{ color: "info.main" }}>
          <strong>{subscription?.campaignLimit}</strong>
        </Box>{" "}
        emails per campaign. Please upgrade to send more or change the number of
        brands selected in this campaign.
      </Typography>
    );
  } else if (
    upgradeDialogSource === UpgradeDialogSource.hits_brand_request_limit
  ) {
    return (
      <Typography paragraph textAlign="center">
        You have made{" "}
        <Box component="span" sx={{ color: "info.main" }}>
          <strong>{brandRequestsMade}</strong>
        </Box>{" "}
        brand requests. Please upgrade to make additional requests.
      </Typography>
    );
  } else if (
    upgradeDialogSource?.includes(
      UpgradeDialogSource.hits_personalized_ai_messages_limit,
    )
  ) {
    return (
      <Typography paragraph textAlign={"center"}>
        <em>
          Upgrade to any of the tiers below to receive{" "}
          <strong>{CHAT_MONTHLY_LIMIT} AI-generated drafts</strong> 🚀 per
          month.
        </em>
      </Typography>
    );
  } else if (upgradeDialogSource === UpgradeDialogSource.exclusive_contact) {
    return (
      <Typography paragraph textAlign="center">
        <Box
          component="i"
          className="fa-regular fa-circle-info"
          sx={{ mr: 1, color: "info.main" }}
        />
        This is <strong>an exclusive contact</strong> (who is 2-3 times more
        likely respond). You need to upgrade to email them.
      </Typography>
    );
  } else if (upgradeDialogSource === UpgradeDialogSource.exclusive_brand) {
    return (
      <Typography paragraph textAlign="center">
        <Box
          component="i"
          className="fa-regular fa-circle-info"
          sx={{ mr: 1, color: "info.main" }}
        />
        This is <strong>an exclusive brand</strong>. Unlock access to 300K+
        brands that other creators can't pitch. Available exclusively to Bento
        subscribers.
      </Typography>
    );
  } else if (
    upgradeDialogSource === UpgradeDialogSource.reply_score ||
    upgradeDialogSource === UpgradeDialogSource.access_partnership_data
  ) {
    return (
      <Grid container justifyContent="center" sx={{ mb: 3 }}>
        {upgradeDialogSource === UpgradeDialogSource.reply_score && (
          <Typography sx={{ mb: 2 }} component="div">
            The <strong>Reply Score</strong> feature shows more information
            about brand contacts such as: <br />
            <ContactListItem
              isActive
              text="If contact is email verified (not bounced)"
            />
            <ContactListItem
              isActive
              text="If contact replied to a user on platform"
            />
            <ContactListItem
              isActive
              text="If contact discussed a specific opportunity with a user"
            />
          </Typography>
        )}
        <Typography textAlign="center" component="div">
          To gain access to this feature, purchase{" "}
          <Link
            sx={{ textDecoration: "none", cursor: "pointer" }}
            onClick={() => {
              trackEvent("Partnership Data Clicked from Reply Score");
              handleCheckout(AddOnItemName.partnership_data);
            }}
          >
            <strong>Partnership Data add-on</strong>
          </Link>{" "}
          or upgrade to our Virtual Assistant plan below.
        </Typography>
      </Grid>
    );
  } else if (
    upgradeDialogSource === UpgradeDialogSource.restricted_filtering_feature
  ) {
    return (
      <Grid container justifyContent="center" sx={{ mb: 3 }}>
        <Typography gutterBottom>
          Subscribe to any of the plans below to unlock more filtering and find
          better-matching brands.
        </Typography>
      </Grid>
    );
  } else {
    return <></>;
  }
}
